/**
 * Parsers: raw bytes/text -> a 2-D string grid (`string[][]`), plus the parse-
 * time row-ceiling policy. Deliberately pure and header-agnostic — structure
 * detection and column matching run afterward on the grid. Blank rows are
 * PRESERVED (never silently dropped) so the junk-row detector can surface them.
 *
 * Both parsers run in Node and the browser; the heavy ones (xlsx, large CSV)
 * are invoked from a Web Worker (parse.worker.ts) so the UI thread never
 * blocks — the functions here are what that worker calls.
 */

import Papa from "papaparse";
import * as XLSX from "xlsx";
import { decodeBytes, detectDelimiter, type Delimiter } from "./sniff";

export interface ParseGrid {
  rows: string[][];
  /** Encoding used when decoding bytes (for CSV/TSV from a file). */
  encoding?: "utf-8" | "windows-1252";
  delimiter?: Delimiter;
}

/** Free-tier soft ceiling; between this and the hard ceiling we nudge. */
export const FREE_ROW_CEILING = 10_000;
/** Hard parse ceiling; above this we reject cleanly. */
export const HARD_ROW_CEILING = 50_000;

export type RowCeilingLevel = "ok" | "nudge" | "reject";
export interface RowCeilingResult {
  level: RowCeilingLevel;
  message?: string;
}

/**
 * Row-ceiling policy (criterion 10, R-4): ok up to the free ceiling, a
 * "save as CSV" nudge up to the hard ceiling, a clean actionable reject above.
 */
export function checkRowCeiling(rowCount: number): RowCeilingResult {
  if (rowCount > HARD_ROW_CEILING) {
    return {
      level: "reject",
      message: `This file has ${rowCount.toLocaleString()} rows, above the ${HARD_ROW_CEILING.toLocaleString()}-row limit. Split it into smaller files, or filter it down, and try again.`,
    };
  }
  if (rowCount > FREE_ROW_CEILING) {
    return {
      level: "nudge",
      message: `Large file (${rowCount.toLocaleString()} rows). If your browser struggles, export it as CSV for a faster, lighter import.`,
    };
  }
  return { level: "ok" };
}

export type FileKind = "workbook" | "delimited";

/** Decide how to parse a file from its name/MIME. xlsx/xls -> workbook, else delimited. */
export function detectFileKind(name: string, mimeType = ""): FileKind {
  if (/\.(xlsx|xls|xlsm|xlsb)$/i.test(name)) return "workbook";
  if (/sheet|excel|officedocument\.spreadsheet/i.test(mimeType)) return "workbook";
  return "delimited";
}

/** Dispatch raw file bytes to the right parser. The Web Worker calls this. */
export function parseBuffer(kind: FileKind, bytes: Uint8Array, delimiter?: Delimiter): ParseGrid {
  return kind === "workbook" ? parseWorkbook(bytes) : parseDelimitedBytes(bytes, delimiter);
}

/** Parse delimited text (delimiter sniffed unless given). Quotes handled by PapaParse. */
export function parseDelimited(text: string, delimiter?: Delimiter): ParseGrid {
  const d = delimiter ?? detectDelimiter(text.slice(0, 64 * 1024));
  const result = Papa.parse<string[]>(text, {
    header: false,
    delimiter: d,
    skipEmptyLines: false, // keep blanks for junk detection
    dynamicTyping: false,
  });
  const rows = (result.data as string[][]).map((row) => row.map((c) => (c ?? "").toString()));
  // PapaParse can append a trailing empty row for a final newline; keep as-is —
  // the junk detector classifies it, we don't drop silently.
  return { rows, delimiter: d };
}

/** Parse a delimited-text FILE (bytes): decode (BOM/Windows-1252 repair) then parse. */
export function parseDelimitedBytes(bytes: Uint8Array, delimiter?: Delimiter): ParseGrid {
  const { text, encoding } = decodeBytes(bytes);
  return { ...parseDelimited(text, delimiter), encoding };
}

/**
 * Parse the first/active sheet of an xlsx/xls workbook to a string grid.
 * `raw: false` + `defval: ""` give consistent stringified cells and preserve
 * blank cells so downstream widths are stable. v1 reads one sheet (M3 scope).
 */
export function parseWorkbook(bytes: Uint8Array): ParseGrid {
  const wb = XLSX.read(bytes, { type: "array" });
  const sheetName = wb.SheetNames[0];
  if (!sheetName) return { rows: [] };
  const sheet = wb.Sheets[sheetName]!;
  const rows = XLSX.utils.sheet_to_json<string[]>(sheet, {
    header: 1,
    raw: false,
    defval: "",
    blankrows: true,
  });
  return { rows: rows.map((row) => row.map((c) => (c ?? "").toString())) };
}
